import React, { useEffect, useRef, useState } from 'react';
import { getAdminBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost } from '../../services/api';
import { Plus, Pencil, Trash2, Loader2, X, HelpCircle, Image as ImageIcon } from 'lucide-react';
import ImageUploader from './ImageUploader';

const emptyForm = {
  title: '',
  slug: '',
  category: 'Rehberler',
  image: '',
  imageAlt: '',
  excerpt: '',
  content: '',
  metaTitle: '',
  metaDescription: '',
  focusKeyphrase: '',
  publishedAt: '',
  updatedAt: '',
  author: { name: 'Baturalp Aktepe', jobTitle: 'Kokartlı Turist Rehberi', bio: "Turist Rehberliği mezunu, 2019'dan bu yana Turizm Bakanlığı'na bağlı ülkesel turist rehberi. On yıla yakın Likya Yolu deneyimi.", image: '' },
  faqItems: [],
  published: false,
};

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('content');
  const [form, setForm] = useState(emptyForm);
  const contentRef = useRef(null);

  const insertInContent = (text) => {
    const el = contentRef.current;
    const current = form.content || '';
    if (!el) {
      setForm({ ...form, content: current + text });
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + text + current.slice(end);
    setForm({ ...form, content: next });
    // restore cursor after re-render
    setTimeout(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    }, 30);
  };

  const fetchPosts = async () => {
    try { const res = await getAdminBlogPosts(); setPosts(res.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPosts(); }, []);

  const generateSlug = (t) => t.toLowerCase()
    .replace(/ü/g,'u').replace(/ö/g,'o').replace(/ş/g,'s').replace(/ç/g,'c').replace(/ğ/g,'g').replace(/ı/g,'i')
    .replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

  const openCreate = () => {
    setForm(emptyForm);
    setTab('content');
    setModal({ mode: 'create' });
  };

  const openEdit = (post) => {
    setForm({
      ...emptyForm,
      ...post,
      author: post.author || emptyForm.author,
      faqItems: Array.isArray(post.faqItems) ? post.faqItems : [],
    });
    setTab('content');
    setModal({ mode: 'edit', id: post.id });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...form };
      if (!data.slug) data.slug = generateSlug(data.title);
      // stamp updatedAt if editing
      if (modal.mode === 'edit') data.updatedAt = new Date().toISOString();
      if (modal.mode === 'create' && !data.publishedAt) data.publishedAt = new Date().toISOString();
      if (modal.mode === 'create') await createBlogPost(data);
      else await updateBlogPost(modal.id, data);
      setModal(null);
      fetchPosts();
    } catch (err) { alert(err.response?.data?.detail || 'Hata'); }
    finally { setSaving(false); }
  };

  const togglePublish = async (post) => {
    await updateBlogPost(post.id, { published: !post.published });
    fetchPosts();
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`"${post.title}" silinecek?`)) return;
    await deleteBlogPost(post.id);
    fetchPosts();
  };

  const updateFaq = (idx, field, value) => {
    const next = [...form.faqItems];
    next[idx] = { ...next[idx], [field]: value };
    setForm({ ...form, faqItems: next });
  };
  const addFaq = () => setForm({ ...form, faqItems: [...form.faqItems, { question: '', answer: '' }] });
  const removeFaq = (idx) => setForm({ ...form, faqItems: form.faqItems.filter((_, i) => i !== idx) });

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#3D4A26]" size={32} /></div>;

  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]';
  const lbl = 'block text-xs font-semibold mb-1 text-gray-700';
  const hint = 'text-[11px] text-gray-400 mt-1';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">{posts.length} yazı</p>
        <button data-testid="admin-blog-new-btn" onClick={openCreate}
          className="flex items-center gap-2 bg-[#C8D843] text-[#1B1B1B] font-semibold px-4 py-2 rounded-lg hover:bg-[#b8c83a] text-sm">
          <Plus size={16} /> Yeni Yazı
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">Henüz blog yazısı yok</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Yazı</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Kategori</th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Durum</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {posts.map(post => (
                <tr key={post.id} className={`hover:bg-gray-50 ${!post.published ? 'opacity-50' : ''}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {post.image && <img src={post.image} alt="" className="w-14 h-10 rounded-lg object-cover" />}
                      <div>
                        <p className="font-semibold text-sm line-clamp-1">{post.title}</p>
                        <p className="text-xs text-gray-500 line-clamp-1">{post.excerpt}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-600">{post.category}</td>
                  <td className="px-5 py-3 text-center">
                    <button onClick={() => togglePublish(post)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${post.published ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                      {post.published ? 'Yayında' : 'Taslak'}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`admin-blog-edit-${post.slug}`} onClick={() => openEdit(post)} className="p-1.5 hover:bg-gray-100 rounded-lg"><Pencil size={16} className="text-gray-400" /></button>
                      <button onClick={() => handleDelete(post)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 size={16} className="text-red-400" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setModal(null)}>
          <form onSubmit={handleSave} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 rounded-t-2xl flex items-center justify-between z-10">
              <h3 className="font-bold text-lg">{modal.mode === 'create' ? 'Yeni Blog Yazısı' : 'Yazı Düzenle'}</h3>
              <button type="button" onClick={() => setModal(null)} className="p-1 hover:bg-gray-100 rounded-full"><X size={18} /></button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-100 px-6 pt-3 flex gap-1">
              {[
                { k: 'content', label: 'İçerik' },
                { k: 'seo', label: 'SEO / AEO' },
                { k: 'faq', label: `SSS (${form.faqItems.length})` },
                { k: 'author', label: 'Yazar' },
              ].map((t) => (
                <button
                  type="button"
                  key={t.k}
                  data-testid={`admin-blog-tab-${t.k}`}
                  onClick={() => setTab(t.k)}
                  className={`px-3 py-2 text-sm font-semibold rounded-t-lg ${tab === t.k ? 'bg-[#F5F5F0] text-[#1B1B1B]' : 'text-gray-500 hover:text-[#1B1B1B]'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-4">
              {tab === 'content' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={lbl}>Başlık (H1) *</label>
                      <input value={form.title} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, title: v, slug: (modal.mode === 'create' && !f.slug) ? generateSlug(v) : f.slug })); }} required className={inp} data-testid="admin-blog-title-input" />
                    </div>
                    <div>
                      <label className={lbl}>Slug (URL)</label>
                      <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className={inp} placeholder="likya-yolu-rehberi" />
                      <p className={hint}>Yıl yazmayın — kalıcı URL için (örn: `likya-yolu-rehberi`)</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={lbl}>Kategori</label>
                      <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className={`${inp} bg-white`}>
                        <option>Rehberler</option>
                        <option>Keşifler</option>
                        <option>Hazırlık & Ekipman</option>
                        <option>Topluluk</option>
                      </select>
                    </div>
                    <div>
                      <label className={lbl}>Yayın Tarihi</label>
                      <input type="datetime-local" value={form.publishedAt ? form.publishedAt.slice(0, 16) : ''} onChange={e => setForm({ ...form, publishedAt: e.target.value ? new Date(e.target.value).toISOString() : '' })} className={inp} />
                    </div>
                  </div>

                  <div>
                    <label className={lbl}>Kapak Görseli</label>
                    {form.image && (
                      <div className="relative mb-2 inline-block">
                        <img src={form.image} alt="" className="w-full max-w-[240px] aspect-video object-cover rounded-lg border border-gray-200" />
                        <button type="button" onClick={() => setForm({ ...form, image: '' })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"><X size={12} /></button>
                      </div>
                    )}
                    <ImageUploader category="blog" label="Kapak Görseli Yükle" onUpload={(url) => setForm(f => ({ ...f, image: url }))} />
                    <input value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} className={`${inp} mt-2`} placeholder="veya URL yapıştır" />
                  </div>

                  <div>
                    <label className={lbl}>Kapak Görsel Alt Metni (Alt Text)</label>
                    <input value={form.imageAlt} onChange={e => setForm({ ...form, imageAlt: e.target.value })} className={inp} placeholder="Örn: Yediburunlar'dan Likya Yolu manzarası" />
                    <p className={hint}>Google Görsel Arama ve erişilebilirlik için önemli</p>
                  </div>

                  <div>
                    <label className={lbl}>Özet</label>
                    <textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} rows={2} className={`${inp} resize-none`} placeholder="Kısa özet — blog listelerinde görünür" />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className={lbl}>İçerik * <span className="font-normal text-gray-400">(Markdown veya HTML)</span></label>
                      <div className="flex items-center gap-2">
                        <ImageUploader
                          category="blog"
                          label={<span className="inline-flex items-center gap-1"><ImageIcon size={12} /> Görsel Ekle</span>}
                          className="!w-auto !px-2.5 !py-1.5 !text-[11px]"
                          onUpload={(url) => insertInContent(`\n\n![Görsel açıklaması](${url})\n\n`)}
                        />
                      </div>
                    </div>
                    <textarea ref={contentRef} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={14} className={`${inp} font-mono text-xs resize-none leading-relaxed`} required data-testid="admin-blog-content-input" placeholder={"# Ana başlık\n\n## Bölüm 1\nMetin...\n\n![Kelebekler Vadisi](https://…/foto.webp)\n\n| Sütun | Değer |\n|---|---|\n| A | 1 |"} />
                    <p className={hint}>Markdown desteği: `# başlık`, `**kalın**`, tablo `| a | b |`, liste `- madde`, link `[metin](url)`, görsel `![alt](url)`. Sistem H2/H3 başlıklarından otomatik &quot;İçindekiler&quot; oluşturur.</p>
                  </div>
                </>
              )}

              {tab === 'seo' && (
                <>
                  <div>
                    <label className={lbl}>Meta Title <span className="font-normal text-gray-400">({(form.metaTitle || '').length}/60)</span></label>
                    <input value={form.metaTitle} onChange={e => setForm({ ...form, metaTitle: e.target.value })} className={inp} maxLength={70} placeholder="Likya Yolu Rehberi 2026: Etaplar, Mevsim ve Konaklama" data-testid="admin-blog-meta-title" />
                    <p className={hint}>Boş bırakırsanız başlık kullanılır. İdeal: 50-60 karakter.</p>
                  </div>
                  <div>
                    <label className={lbl}>Meta Description <span className="font-normal text-gray-400">({(form.metaDescription || '').length}/155)</span></label>
                    <textarea value={form.metaDescription} onChange={e => setForm({ ...form, metaDescription: e.target.value })} rows={3} className={`${inp} resize-none`} maxLength={170} placeholder="Likya Yolu kaç km, hangi etaplardan oluşuyor, ne zaman yürünür? 2026 orman yasağı, konaklama fiyatları..." data-testid="admin-blog-meta-desc" />
                    <p className={hint}>İdeal: 140-155 karakter. Google arama sonucunda görünen kısım.</p>
                  </div>
                  <div>
                    <label className={lbl}>Odak Anahtar Kelime (Focus Keyphrase)</label>
                    <input value={form.focusKeyphrase} onChange={e => setForm({ ...form, focusKeyphrase: e.target.value })} className={inp} placeholder="likya yolu rehberi" />
                    <p className={hint}>Bu yazının hedef aldığı ana anahtar kelime.</p>
                  </div>
                  <div className="bg-[#F5F5F0] rounded-lg p-4 text-xs text-gray-600 leading-relaxed">
                    <p className="font-semibold text-[#3D4A26] mb-2">🎯 AEO (AI Answer Engine Optimization) İpuçları</p>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>İçeriğinizin başında bir &quot;Kısa Cevaplar&quot; tablosu ekleyin.</li>
                      <li>H2 başlıklarını doğrudan bir soru olarak yazın (örn: &quot;Likya Yolu kaç kilometre?&quot;).</li>
                      <li>Cevaplar 40-70 kelime arasında olsun.</li>
                      <li>SSS sekmesinden en az 8-10 sık sorulan soru ekleyin.</li>
                    </ul>
                  </div>
                </>
              )}

              {tab === 'faq' && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-[#1B1B1B]">Sıkça Sorulan Sorular (FAQPage Schema)</p>
                      <p className="text-xs text-gray-500">Google AI Overviews için kritik. En az 6-8 soru önerilir.</p>
                    </div>
                    <button type="button" onClick={addFaq} className="flex items-center gap-1.5 bg-[#3D4A26] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#2d3a1a]" data-testid="admin-blog-faq-add">
                      <Plus size={14} /> Soru Ekle
                    </button>
                  </div>

                  {form.faqItems.length === 0 && (
                    <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
                      <HelpCircle className="mx-auto text-gray-300 mb-2" size={32} />
                      <p className="text-sm text-gray-500">Henüz soru eklenmedi</p>
                    </div>
                  )}

                  {form.faqItems.map((f, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-2 bg-[#FAFAF7]">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-semibold text-[#3D4A26]">Soru #{i + 1}</span>
                        <button type="button" onClick={() => removeFaq(i)} className="text-red-500 hover:bg-red-50 rounded p-1"><X size={14} /></button>
                      </div>
                      <input value={f.question} onChange={e => updateFaq(i, 'question', e.target.value)} className={inp} placeholder="Örn: Likya Yolu kaç kilometre?" />
                      <textarea value={f.answer} onChange={e => updateFaq(i, 'answer', e.target.value)} rows={3} className={`${inp} resize-none`} placeholder="Cevap (40-70 kelime ideal)" />
                    </div>
                  ))}
                </>
              )}

              {tab === 'author' && (
                <>
                  <p className="text-xs text-gray-500 mb-2">Yazar bilgileri E-E-A-T sinyalidir. Google için &quot;gerçek bir uzman yazmış&quot; güvencesi verir.</p>
                  <div>
                    <label className={lbl}>Yazar Adı</label>
                    <input value={form.author.name} onChange={e => setForm({ ...form, author: { ...form.author, name: e.target.value } })} className={inp} />
                  </div>
                  <div>
                    <label className={lbl}>Ünvan / Görev</label>
                    <input value={form.author.jobTitle} onChange={e => setForm({ ...form, author: { ...form.author, jobTitle: e.target.value } })} className={inp} placeholder="Kokartlı Turist Rehberi" />
                  </div>
                  <div>
                    <label className={lbl}>Kısa Biyografi</label>
                    <textarea value={form.author.bio} onChange={e => setForm({ ...form, author: { ...form.author, bio: e.target.value } })} rows={4} className={`${inp} resize-none`} />
                  </div>
                  <div>
                    <label className={lbl}>Yazar Fotoğrafı (opsiyonel)</label>
                    <ImageUploader category="blog" label="Fotoğraf Yükle" onUpload={(url) => setForm(f => ({ ...f, author: { ...f.author, image: url } }))} />
                    {form.author.image && <img src={form.author.image} alt="" className="w-16 h-16 rounded-full object-cover mt-2" />}
                  </div>
                </>
              )}

              <label className="flex items-center gap-2 cursor-pointer pt-3 border-t border-gray-100 mt-4">
                <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[#C8D843]" />
                <span className="text-sm font-medium">Yayınla (görünür yap)</span>
              </label>
            </div>

            <div className="px-6 pb-6">
              <button type="submit" disabled={saving} className="w-full bg-[#3D4A26] text-white font-bold py-3 rounded-lg hover:bg-[#2d3a1a] transition-colors disabled:opacity-50" data-testid="admin-blog-save-btn">
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
